import { useContext, useEffect, useMemo } from "react";
import CompanyService, { Entities } from "../../../../services/CompanyService";
import { ClientFields } from "../../../../services/ClientService";
import { PickByValue } from "../../../../utils/Utils";
import ToastContext from "../../../shared/bootstrap/Toast";
import Dropdown from "../../../shared/components/Dropdown";
import { CheckBoxEditor } from "../../../shared/components/Editors";
import { ValidationMessage } from "../../../shared/RequieredManager";
import { ImportTemplateListResponse } from "../../../shared/entities/ImportTemplateListResponse";
import { TranslationService } from "../../../../services/TranslationService";
import Alarm from "./Components/Alarm";
import DateFormat from "./Components/DateFormat";
import Dictionaries from "./Components/Dictionaries";
import EditItem from "./Components/EditItem";
import ExcelOptions from "./Components/ExcelOptions";
import PrecedingProcesses from "./Components/PrecedingProcesses";
import SeparatorWithFixedColumns from "./Components/SeparatorWithFixedColumns";
import UploadTemplate from "./Components/UploadTemplate";
import ImportTemplateEditBase, {
  ImportTemplateComponentProps
} from "./ImportTemplateEditBase";

const ImportTemplateEditClient = ({
  data,
  reload,
  importsList = undefined,
  entityId = undefined
}: {
  data?: ImportTemplateListResponse.Item;
  reload: () => void;
  importsList?: ImportTemplateListResponse;
  entityId?: number;
}) => {
  const { translate } = TranslationService;
  const personKey = CompanyService.getPersonKey();
  const requiredIds: string[] =
    personKey > 0 &&
    !CompanyService.getSetting("customfieldpersonimport") &&
    !CompanyService.getSetting("allowcustomfieldimport")
      ? [personKey.toString()]
      : [];
  const addFields = useMemo(() => {
    return CompanyService.getAdditionalDefinitions().filter(
      (x) => x.Entity === Entities.Client
    );
  }, []);

  const Header = () => {
    return (
      <div className="my-3">
        <h5 className="mb-4">{translate.ConfigureCustomerDataUpdate}</h5>
      </div>
    );
  };

  const AdvancedOptions = ({
    data,
    updateModel,
    model,
    setModel,
    requiredManager
  }: ImportTemplateComponentProps) => {
    const { showToast } = useContext(ToastContext);
    const updateNumberModel =
      <
        T extends keyof PickByValue<
          ImportTemplateListResponse.Item,
          number | undefined
        >
      >(
        key: T
      ) =>
      (value?: number) => {
        setModel((x) => ({ ...x, [key]: value }));
      };

    const allowCustomFieldImport = !!CompanyService.getSetting(
      "allowcustomfieldimport"
    );

    const customPersonIdentityOnChange = requiredManager.makeRequiredIf(
      allowCustomFieldImport,
      updateNumberModel("CustomPersonIdentityID"),
      "validationCustomPersonIdentityID_required"
    );

    useEffect(() => {
      if (!allowCustomFieldImport) {
        setModel((x) =>
          x.CustomPersonIdentityID != null
            ? { ...x, CustomPersonIdentityID: null }
            : x
        );
      }
    }, [allowCustomFieldImport, setModel]);

    const getColumnName = (id: number): string => {
      if (id > 0) return "additional-" + id;
      const fixedFieldMap: Record<number, string> = {
        [ClientFields.Name]: "name",
        [ClientFields.Contact]: "contact",
        [ClientFields.Email]: "email",
        [ClientFields.Phone]: "phone",
        [ClientFields.Address]: "address",
        [ClientFields.Comments]: "comments",
        [ClientFields.CreditLimit]: "creditlimit"
      };
      return fixedFieldMap[id] ?? id.toString();
    };

    const dropdownItems = [
      ...addFields.map((x) => ({
        value: x.AdditionalDefinitionID,
        text: x.Name
      }))
    ];

    const customValidation = requiredManager.getValidation(
      "validationCustomPersonIdentityID"
    );
    customValidation.isValid = () => {
      if (!model.CustomPersonIdentityID) {
        return true;
      }
      const columnName = getColumnName(model.CustomPersonIdentityID as number);
      const foundColumn = model.Columns?.find((x) => x.name === columnName);
      const valid = foundColumn !== undefined && !!foundColumn.ix;
      if (!valid) {
        showToast(translate.TheFieldSelectedForUpdateMustHaveASpecifiedColumn);
      }
      return valid;
    };

    return (
      <div>
        <small className="text-muted mb-3">
          {translate.IfYouLeaveFormatBlank}
        </small>
        <DateFormat
          defaultValue={data?.DateFormat}
          updateModel={updateModel("DateFormat")}
        />
        <CheckBoxEditor
          message={translate.UpdateExistingCustomerData}
          onChange={updateModel("UpdateIOs")}
          defaultValue={data?.UpdateIOs}
        />
        {CompanyService.canEditTemplate() && (
          <UploadTemplate model={model} updateModel={updateModel} />
        )}
        <SeparatorWithFixedColumns
          updateModel={updateModel}
          data={data}
          model={model}
        />
        <ExcelOptions data={data} updateModel={updateModel} />
        {CompanyService.getSetting("allowimportdictionary") && (
          <>
            <hr className="my-5" />
            <Dictionaries data={data} model={model} setModel={setModel} />
          </>
        )}
        {CompanyService.getSetting("allowcustomfieldimport") && (
          <>
            <hr className="my-5" />
            <EditItem label={translate.UpdateDataUsingTheField}>
              <Dropdown
                onChange={customPersonIdentityOnChange}
                defaultValue={data?.CustomPersonIdentityID}
                items={dropdownItems}
                optionLabel={translate.Select}
              />
              <ValidationMessage
                onChange={requiredManager.getValidation(
                  "validationCustomPersonIdentityID_required"
                )}
                defaultValue={model?.CustomPersonIdentityID}
              />
            </EditItem>
          </>
        )}
        {CompanyService.getSetting("allowimportpredecessor") && (
          <>
            <hr className="my-5" />
            <PrecedingProcesses
              data={data}
              model={model}
              setModel={setModel}
              importsList={importsList}
            />
          </>
        )}
        <hr className="my-5" />
        <Alarm
          data={data}
          updateModel={updateModel}
          setModel={setModel}
          model={model}
        />
      </div>
    );
  };

  return (
    <ImportTemplateEditBase
      entityId={entityId}
      AdvancedOptions={AdvancedOptions}
      Header={Header}
      reload={reload}
      requiredFieldIds={requiredIds}
      data={data}
      showName
    />
  );
};

export default ImportTemplateEditClient;
